import * as http from '@angular/common/http'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as me from '../me'

import * as gql from './gql'
import * as models from './models'

/**
 * API service for file uploads
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Api {
  /** @see {@link http.HttpClient} */
  private readonly _http = ngCore.inject(http.HttpClient)

  /** @see {@link me.Feature} */
  private readonly _meFeature = ngCore.inject(me.Feature)

  /**
   * Requests a presigned POST URL from the backend for uploading a file
   */
  public getUploadRequest(
    request: Omit<models.FileRequest.ForUpload, 'file'> & {
      file: File
      fileName?: string
    },
  ): rxjs.Observable<void> {
    if (!ents.core.admin.Context.ForZone.isForZone(request.context)) {
      throw new Error('TODO')
    }

    const { extension, name } = models.FileRequest.getFileInfo(
      request.fileName ?? request.file,
    )
    return gql.queries.uploadRequest
      .execute({
        fileInput: {
          ...getFileContextInput(request.context),
          extension,
          id: name,
          purpose: gql.enums.filePurpose.TRANSFORMER.getGqlFrom(
            request.purpose,
          ),
          qualifier: request.qualifier,
        },
        tenantId: request.context.tenantId,
        zoneId: request.context.zoneId,
      })
      .pipe(
        rxjs.switchMap(presignedRequest => {
          const formData = new FormData()
          if (presignedRequest.fields) {
            for (const key in presignedRequest.fields) {
              formData.append(key, presignedRequest.fields[key])
            }
          }
          // NOTE: this must appear last (at least for mocking, potentially also
          // for S3)
          formData.append('file', request.file)
          return (
            this._http
              .post(presignedRequest.url, formData, {
                responseType: 'text',
              })
              // return void
              .pipe(rxjs.map(() => undefined))
          )
        }),
      )
  }

  /**
   * Requests a presigned GET URL from the backend for downloading a file
   */
  public getDownloadRequest(
    request: models.FileRequest.ForDownload,
  ): rxjs.Observable<string> {
    if (!ents.core.admin.Context.ForZone.isForZone(request.context)) {
      throw new Error('TODO')
    }

    return gql.queries.dataDownloadRequest.execute({
      fileInput: {
        ...getFileContextInput(request.context),
        extension: request.extension,
        id: request.id,
        purpose: gql.enums.filePurpose.TRANSFORMER.getGqlFrom(request.purpose),
        qualifier: request.qualifier,
      },
      tenantId: request.context.tenantId,
      zoneId: request.context.zoneId,
    })
  }

  /** Retrieves File Management object metadata for a zone. */
  public listFinderFiles(
    context: ents.core.admin.Context.ForZone,
  ): rxjs.Observable<readonly models.File[]> {
    const currentUserId = this._meFeature.selectors.currentUserId.asSignal()
    return gql.queries.files.execute(context).pipe(
      rxjs.map(files =>
        files.map(file => ({
          context: getFileContext(context, currentUserId, file),
          extension: file.extension,
          id: file.id,
          qualifier: file.qualifier,
        })),
      ),
    )
  }
}

/** Converts a file key context into the GraphQL file-input context fields. */
function getFileContextInput(
  context: ents.core.common.fileManagement.Key.Parts.Context,
): Pick<graphql.schema.FileInput, 'appId' | 'isPrivate' | 'scenarioId'> {
  const { appId, scenarioId }: ents.core.admin.Context = context
  return {
    appId,
    isPrivate: ents.core.admin.Context.ForUser.isForUser(context),
    scenarioId,
  }
}

/** Rebuilds the full S3 key context returned by a File Management listing. */
function getFileContext(
  zoneContext: ents.core.admin.Context.ForZone,
  currentUserId: ents.core.admin.User.Id | undefined,
  file: models.File.Listed,
): ents.core.common.fileManagement.Key.Parts.Context {
  const context = file.isPrivate
    ? {
        ...zoneContext,
        userId: TypeAsserter.validate(
          currentUserId,
          TypeAsserter.assertIsNotNullish,
        ),
      }
    : zoneContext

  return ents.core.admin.Context.ForAny.fromIds({
    ...context,
    appId: file.appId,
    scenarioId: file.scenarioId,
  })
}
